/**
 * NotificationForm Component
 * Unified form for sending notifications with template or direct content
 */

import { useEffect } from 'react';
import clsx from 'clsx';
import { Variable } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { ChannelPicker } from './ChannelPicker';
import { ActionPicker } from './ActionPicker';
import { TemplatePicker } from './TemplatePicker';
import { NotificationPreview } from './NotificationPreview';
import { useNotificationTemplate } from '../hooks/useNotificationTemplates';
import type { Channel, NotificationContent, TemplateVariable } from '../types';

/**
 * Render template content with variable highlighting
 */
function highlightVariables(text: string): React.ReactNode {
  if (!text) return null;
  const parts = text.split(/(\{\w+\})/g);
  return parts.map((part, i) => {
    if (part.match(/^\{\w+\}$/)) {
      return (
        <code key={i} className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-mono">
          {part}
        </code>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export interface NotificationFormValue {
  template: string | null;
  channels: Channel[];
  content: NotificationContent;
  context: Record<string, string>;
  overrides: Record<string, unknown>;
}

interface NotificationFormProps {
  mode?: 'template' | 'direct' | 'both';
  value: NotificationFormValue;
  onChange: (value: NotificationFormValue) => void;
  disabled?: boolean;
  className?: string;
}

export function NotificationForm({
  mode = 'both',
  value,
  onChange,
  disabled,
  className,
}: NotificationFormProps) {
  // Fetch selected template details
  const { data: selectedTemplate } = useNotificationTemplate(value.template || '');

  // Get defined variables from selected template
  const templateVariables: TemplateVariable[] = selectedTemplate?.variables || [];

  // Update form when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      onChange({
        ...value,
        channels: selectedTemplate.channels,
        content: selectedTemplate.content,
      });
    }
  }, [selectedTemplate]);

  const handleTemplateChange = (templateName: string | null) => {
    onChange({
      ...value,
      template: templateName,
      // Reset channels and content if switching away from template
      ...(templateName === null && {
        channels: [],
        content: { title: '', body: '' },
      }),
    });
  };

  const handleChannelsChange = (channels: Channel[]) => {
    onChange({ ...value, channels });
  };

  const handleContentChange = (field: keyof NotificationContent, fieldValue: string) => {
    onChange({
      ...value,
      content: { ...value.content, [field]: fieldValue },
    });
  };

  const handleVariableChange = (varName: string, varValue: string) => {
    onChange({
      ...value,
      context: { ...value.context, [varName]: varValue },
    });
  };

  const isUsingTemplate = value.template !== null;
  const currentChannels = value.channels || [];
  const currentContent = value.content || { title: '', body: '' };

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Template Selection */}
      {(mode === 'template' || mode === 'both') && (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-gray-900">选择通知模板</h3>
          <TemplatePicker
            value={value.template}
            onChange={handleTemplateChange}
            disabled={disabled}
          />
        </div>
      )}

      {/* Divider */}
      {mode === 'both' && (
        <div className="border-t border-gray-200" />
      )}

      {/* Channel Selection - only show when not using template */}
      {(mode === 'direct' || (mode === 'both' && !isUsingTemplate)) && (
        <ChannelPicker
          value={currentChannels}
          onChange={handleChannelsChange}
          disabled={disabled}
        />
      )}

      {/* Content Editing - only show when not using template */}
      {(mode === 'direct' || (mode === 'both' && !isUsingTemplate)) && (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-gray-900">通知内容</h3>

          {/* Title - only for push channel */}
          {currentChannels.includes('push') && (
            <Input
              label="标题"
              value={currentContent.title || ''}
              onChange={e => handleContentChange('title', e.target.value)}
              placeholder="推送通知标题"
              disabled={disabled}
            />
          )}

          {/* Body */}
          <Textarea
            label="正文"
            value={currentContent.body || ''}
            onChange={e => handleContentChange('body', e.target.value)}
            placeholder="通知正文内容"
            rows={3}
            disabled={disabled}
          />

          {/* Icon - for realtime channel */}
          {currentChannels.includes('realtime') && (
            <Input
              label="图标 (Ionicon 名称)"
              value={currentContent.icon || ''}
              onChange={e => handleContentChange('icon', e.target.value)}
              placeholder="例如: sparkles, heart, information-circle"
              disabled={disabled}
              helperText="使用 Ionicons 图标名称，如 sparkles, heart, star 等"
            />
          )}

          {/* Action */}
          <ActionPicker
            value={currentContent.action || ''}
            onChange={action => handleContentChange('action', action)}
            disabled={disabled}
          />

          {/* Action Label - shown when action is selected */}
          {currentContent.action && (
            <Input
              label="操作按钮文字"
              value={currentContent.action_label || ''}
              onChange={e => handleContentChange('action_label', e.target.value)}
              placeholder="查看"
              disabled={disabled}
            />
          )}
        </div>
      )}

      {/* Template Variables - show when using template */}
      {isUsingTemplate && selectedTemplate && (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-gray-900">模板变量</h3>

          {/* Show template content with highlighted variables */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-3">模板内容预览:</p>
            <div className="space-y-2 text-sm">
              {selectedTemplate.content.title && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 text-xs font-medium w-10 shrink-0 pt-0.5">标题</span>
                  <span className="text-gray-700">{highlightVariables(selectedTemplate.content.title)}</span>
                </div>
              )}
              <div className="flex items-start gap-2">
                <span className="text-gray-400 text-xs font-medium w-10 shrink-0 pt-0.5">正文</span>
                <span className="text-gray-700">{highlightVariables(selectedTemplate.content.body || '')}</span>
              </div>
            </div>
          </div>

          {/* Variable input fields */}
          {templateVariables.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <Variable className="w-4 h-4" />
                填写以下变量的值:
              </p>
              <div className="grid grid-cols-1 gap-3">
                {templateVariables.map(variable => (
                  <Input
                    key={variable.name}
                    label={
                      <span className="flex items-center gap-2">
                        <code className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-xs font-mono">
                          {`{${variable.name}}`}
                        </code>
                        <span className="text-gray-700">{variable.label}</span>
                        {variable.required && <span className="text-red-500">*</span>}
                      </span>
                    }
                    value={value.context[variable.name] || variable.default || ''}
                    onChange={e => handleVariableChange(variable.name, e.target.value)}
                    placeholder={variable.description || `输入 ${variable.label}`}
                    disabled={disabled}
                    required={variable.required}
                    helperText={variable.description}
                    type={variable.type === 'number' ? 'number' : 'text'}
                  />
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
              此模板没有需要填写的变量
            </p>
          )}
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Preview */}
      <NotificationPreview
        channels={currentChannels}
        content={currentContent}
      />
    </div>
  );
}

// Default value factory
export function createDefaultNotificationFormValue(): NotificationFormValue {
  return {
    template: null,
    channels: [],
    content: {
      title: '',
      body: '',
    },
    context: {},
    overrides: {},
  };
}
